import React from "react";
import {
    AlertCircle,
    ArrowRight,
    CircleHelp,
    ListChecks,
    CheckCircle2,
} from "lucide-react";
import GuideInfoBlock from "./GuideInfoBlock";
import GuideSteps from "./GuideSteps";
import GuideTextList from "./GuideTextList";
import GuideVideo from "./GuideVideo";

const renderGuideContent = (content) => {
    if (!content) return null;

    if (typeof content === "string") {
        return <p className="text-sm leading-6 text-slate-600">{content}</p>;
    }

    if (Array.isArray(content)) {
        return <GuideTextList items={content} />;
    }

    return (
        <div className="space-y-3">
            {content.description && (
                <p className="text-sm leading-6 font-medium text-slate-600">
                    {content.description}
                </p>
            )}
            <GuideTextList items={content.items} />
        </div>
    );
};

const renderNextStepContent = (nextStep, onSelectGuide) => {
    if (!nextStep) return null;

    const canSwitchGuide =
        typeof onSelectGuide === "function" && Boolean(nextStep.guideKey);

    if (!nextStep.label) {
        return renderGuideContent(nextStep);
    }

    return (
        <div className="space-y-3">
            {nextStep.description && (
                <p className="text-sm font-medium leading-6 text-slate-600">
                    {nextStep.description}
                </p>
            )}
            <p className="text-sm leading-6 text-slate-700">
                Next recommended feature:{" "}
                {canSwitchGuide ? (
                    <button
                        type="button"
                        onClick={() => onSelectGuide(nextStep.guideKey)}
                        className="font-medium text-indigo-700 transition hover:text-indigo-800 hover:underline focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-indigo-200"
                    >
                        {nextStep.label}
                    </button>
                ) : (
                    <span className="font-medium text-indigo-700">
                        {nextStep.label}
                    </span>
                )}
            </p>
        </div>
    );
};

function GuideSection({ guide, onSelectGuide }) {
    const {
        title,
        shortDescription,
        whatIsIt,
        whyYouNeedIt,
        steps = [],
        importantNotes = [],
        video,
        nextStep,
    } = guide || {};

    return (
        <section
            id="guide-content"
            role="tabpanel"
            className="mt-5 space-y-5"
        >
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-600">
                        Guide
                    </p>
                    <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                        {title}
                    </h2>
                    {shortDescription && (
                        <p className="mt-3 max-w-3xl text-sm font-medium leading-6 text-slate-600">
                            {shortDescription}
                        </p>
                    )}
                </div>

                {(video?.videoId || video?.url || video?.youtubeUrl) && (
                    <GuideVideo
                        key={video.videoId || video.url || video.youtubeUrl}
                        videoId={video.videoId}
                        youtubeUrl={video.youtubeUrl || video.url}
                        thumbnailUrl={video.thumbnailUrl}
                        title={video.title || title}
                    />
                )}
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
                <GuideInfoBlock title="What is this feature?" icon={CircleHelp}>
                    {renderGuideContent(whatIsIt)}
                </GuideInfoBlock>
                <GuideInfoBlock title="Why do you need it?" icon={CheckCircle2}>
                    {renderGuideContent(whyYouNeedIt)}
                </GuideInfoBlock>
                <GuideInfoBlock title="Important notes" icon={AlertCircle}>
                    <GuideTextList
                        items={importantNotes}
                        iconClassName="text-amber-600"
                    />
                </GuideInfoBlock>
            </div>

            <GuideInfoBlock title="How to use it" icon={ListChecks}>
                <div
                    className={`grid gap-5 ${
                        nextStep
                            ? "lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.45fr)]"
                            : ""
                    }`}
                >
                    <GuideSteps steps={steps} />

                    {nextStep && (
                        <aside className="min-w-0 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 shadow-sm">
                            <div className="mb-3 flex items-center gap-2">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-600 ring-1 ring-indigo-100">
                                    <ArrowRight size={16} aria-hidden="true" />
                                </span>
                                <h4 className="text-sm font-bold text-slate-900">
                                    What&apos;s next
                                </h4>
                            </div>
                            {renderNextStepContent(nextStep, onSelectGuide)}
                        </aside>
                    )}
                </div>
            </GuideInfoBlock>
        </section>
    );
}

export default GuideSection;
